import DataService, { DataStoreType } from '../../idscan/classes/DataService';
import { JourneyEvent } from '../../idscan/enums/JourneyEvent';
import { TranslationKey } from '../../idscan/enums/Translation';
import { IIdScanData } from '../../idscan/interfaces/DataFormats';
import { IEventSubscriber } from '../../idscan/interfaces/EventSubscriber';
import { IUnsubscribable } from '../../idscan/interfaces/Unsubscribable';
import { ExceptionManager } from './ExceptionManager';

/**
 * @internal
 */
export class EventService {
	private _topics: Partial<Record<JourneyEvent, IEventSubscriber[]>> = {};

	/**
	 * Publishes an event for the listeners, and feeds the function with the given data
	 * @param event
	 * @param data
	 */
	public publish = (event: JourneyEvent, data?: any): Promise<void> => new Promise((resolve) => {
		(this._topics[event] || []).forEach((subscriber) => {
			subscriber.fn({ timestamp: new Date().getTime(), ...data });
		});

		resolve();
	})

	/**
	 * Subscribes to a specific event with a bound function, returns an Unsubscribable.
	 * @param event
	 * @param fn
	 * @param priority
	 */
	public subscribe(event: JourneyEvent, fn: any, priority?: number): IUnsubscribable {
		this.assertStringEvent(event);
		this.assertValidFunction(fn);
		this.assertValidPriority(priority);

		const subscriber: IEventSubscriber = {
			fn,
			priority
		};

		if (!this._topics[event]) {
			this._topics[event] = [];
		}

		this._topics[event]!.push(subscriber);
		this.sort(event);

		return {
			unsubscribe: () => {
				this.unsubscribe(event, subscriber);
			}
		};
	}

	public terminate = () => new Promise((resolve) => {
		DataService.getDataStore<IIdScanData>(DataStoreType.ID_SCAN).reset();
		this.unsubscribeAll()
			.then(resolve);
	})

	public unsubscribeAll = () => new Promise<void>((resolve) => {
		Object.keys(this._topics).forEach((topic) => {
			(this._topics as Record<JourneyEvent, IEventSubscriber[]>)[topic as JourneyEvent]
				.forEach((sub) => this.unsubscribe(topic, sub));
		});

		return resolve();
	})

	unsubscribeTopic(event: JourneyEvent) {
		var topic = (this._topics as Record<JourneyEvent, IEventSubscriber[]>)[event as JourneyEvent];

		if (topic) {
			topic.forEach((sub) => this.unsubscribe(event, sub));
		}
	}

	/**
	 * Unsubscribes a function from being triggered in case of an event
	 * @param event
	 * @param subscriber
	 */
	public unsubscribe(event: string, subscriber: IEventSubscriber): void {
		if (this._topics[event as JourneyEvent]) {
			this._topics[event as JourneyEvent] = this._topics[event as JourneyEvent]!.filter((sub: any) => subscriber.fn !== sub.fn);
		}
	}

	/**
	 * An algorithm to sort given event subscribers according to their priority
	 * @param event
	 */
	private sort(event: string) {
		if (this._topics[event as JourneyEvent]) {
			this._topics[event as JourneyEvent]!.sort((a: IEventSubscriber, b: IEventSubscriber) => {
				return a.priority !== undefined && b.priority !== undefined ?
					a.priority > b.priority ?
						1 :
						b.priority > a.priority ?
							-1 :
							0 :
					a.priority === undefined && b.priority === undefined ?
						0 :
						a.priority === undefined ?
							1 :
							-1;
			});
		}
	}

	/**
	 * Makes sure that the event name is string
	 * @param event
	 */
	private assertStringEvent(event: string) {
		if (typeof event !== 'string') {
			throw ExceptionManager.process(new ExceptionManager.InvalidParameterException(TranslationKey.EXCEPTION_INVALID_PARAMETER_NAME_MUST_BE_STRING));
		}
	}

	/**
	 * Make sure that the callback paramter is a valid function
	 * @param fn
	 */
	private assertValidFunction(fn: any) {
		if (fn instanceof Function === false) {
			throw ExceptionManager.process(new ExceptionManager.InvalidParameterException(TranslationKey.EXCEPTION_INVALID_PARAMETER_CALLBACK_MUST_BE_FUNCTION));
		}
	}

	/**
	 * Make sure that the priority is a positive number or undefined
	 * @param priority
	 */
	private assertValidPriority(priority: number | undefined) {
		if (priority !== undefined) {
			if (typeof priority !== 'number') {
				throw ExceptionManager.process(new ExceptionManager.InvalidParameterException(TranslationKey.EXCEPTION_INVALID_PARAMETER_PRIORITY_MUST_BE_NUMBER));
			} else if (priority < 0) {
				throw ExceptionManager.process(new ExceptionManager.InvalidParameterException(TranslationKey.EXCEPTION_INVALID_PARAMETER_PRIORITY_MUST_BE_POSITIVE_NUMBER));
			}
		}
	}
}

export default new EventService();
